import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Container } from 'react-bootstrap';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import FloatingActionButton from './components/FloatingActionButton';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PromptsPage from './pages/PromptsPage';
import PromptDetailPage from './pages/PromptDetailPage';
import CreatePromptPage from './pages/CreatePromptPage';
import EditPromptPage from './pages/EditPromptPage';
import ProfilePage from './pages/ProfilePage';
import TagsPage from './pages/TagsPage';
import AdminPage from './pages/AdminPage';
import NotFoundPage from './pages/NotFoundPage';

// Protected Route Component
const ProtectedRoute = ({ children, roles = [] }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// App Layout Component
const AppLayout = ({ children }) => {
  const { user } = useAuth();

  return (
    <div className="d-flex flex-column min-vh-100">
      <Navbar />
      <main className="flex-grow-1 py-4">
        <Container fluid>
          {children}
        </Container>
      </main>
      <Footer />
      {user && <FloatingActionButton />}
    </div>
  );
};

// Main App Component
const AppContent = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        <AppLayout>
          <HomePage />
        </AppLayout>
      } />
      
      <Route path="/login" element={<LoginPage />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout>
            <DashboardPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/prompts" element={
        <ProtectedRoute>
          <AppLayout>
            <PromptsPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/prompts/:id" element={
        <ProtectedRoute>
          <AppLayout>
            <PromptDetailPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/prompts/create" element={
        <ProtectedRoute>
          <AppLayout>
            <CreatePromptPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/prompts/:id/edit" element={
        <ProtectedRoute>
          <AppLayout>
            <EditPromptPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/profile" element={
        <ProtectedRoute>
          <AppLayout>
            <ProfilePage />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/tags" element={
        <ProtectedRoute>
          <AppLayout>
            <TagsPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute roles={['admin']}>
          <AppLayout>
            <AdminPage />
          </AppLayout>
        </ProtectedRoute>
      } />
      
      {/* 404 Route */}
      <Route path="*" element={
        <AppLayout>
          <NotFoundPage />
        </AppLayout>
      } />
    </Routes>
  );
};

// Root App Component
const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App;
